"use client";

import { useDescope as _useDescope, useSession as _useSession, useUser as _useUser } from "@descope/nextjs-sdk/client";

import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { useCallback } from "react";

import { LINKS } from "@/config/links";

import { invalidateQueryCache } from "@/lib/integrations/react-query";

import { useOnboardingPermissions } from "@/subsystems/access/onboarding/hooks/use-onboarding-permissions";

import { dispatchAgoraEvent } from "../utils/dispatch-agora-event";

export function useDescopeLogout() {
  const sdk = _useDescope();
  const router = useRouter();
  const onboardingPermissions = useOnboardingPermissions();

  const goToLoginPage = useCallback(() => {
    router.push(LINKS.LOGIN);
  }, [router]);

  const goToLogoutPage = useCallback(() => {
    window.location.href = LINKS.LOGOUT;
  }, []);

  const logoutPageActions = useCallback(
    (turnkeyLogout?: () => Promise<unknown>) => {
      console.log("logging out...");

      // https://posthog.com/docs/getting-started/identify-users#3-reset-after-logout
      posthog.reset();

      sdk
        .logoutAll()
        .then(invalidateQueryCache)
        .finally(async () => {
          dispatchAgoraEvent({
            eventAction: "auth.logout.succeeded",
            eventKey: onboardingPermissions?.canEnterInternalPortal ? "internal-logged-in" : "customer-logged-in",
            eventLocation: "global",
            eventType: "product.form.submitted",
            eventDetails: {
              isDescope: true,
            },
          });

          // Clear the Turnkey session before leaving, so a re-login starts clean.
          await turnkeyLogout?.();

          goToLoginPage();
        });
    },
    [goToLoginPage, onboardingPermissions?.canEnterInternalPortal, sdk],
  );

  return {
    goToLogoutPage,
    logoutPageActions,
  };
}

export function useUser() {
  // It's required that you call both! This makes sure the session is refreshed.
  const { isSessionLoading } = _useSession();
  const { isUserLoading, user } = _useUser();

  return { isUserLoading: isUserLoading || isSessionLoading, user };
}
